import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { 
  Handshake, Plus, Search, Building2, GraduationCap, Globe, 
  Users, Calendar, FileText, Trash2, RefreshCw, Eye, Activity,
  Building, Landmark, Heart, ExternalLink
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Map URL paths to tab values
const getTabFromPath = (pathname) => {
  if (pathname.includes('/mou/activities')) return 'activities';
  if (pathname.includes('/mou/analytics')) return 'analytics';
  return 'mous';
};

export default function MoUManagement() {
  const location = useLocation();
  const [activeTab, setActiveTab] = useState(() => getTabFromPath(location.pathname));
  const [mous, setMous] = useState([]);
  const [activities, setActivities] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  
  const [showMoUModal, setShowMoUModal] = useState(false);
  const [showActivityModal, setShowActivityModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedMoU, setSelectedMoU] = useState(null);
  const [editMode, setEditMode] = useState(false);
  
  const [mouForm, setMouForm] = useState({
    title: '', partner_name: '', partner_type: 'industry', partner_country: 'India',
    purpose: '', scope: '', start_date: '', end_date: '', duration_years: '',
    status: 'active', signatory_institution: '', signatory_partner: '',
    activities: [], outcomes: '', students_benefited: '', faculty_involved: '',
    contact_person: '', contact_email: '', remarks: ''
  });
  
  const [activityForm, setActivityForm] = useState({
    mou_id: '', activity_type: 'workshop', title: '', description: '',
    date: '', participants_count: '', outcomes: ''
  });

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  // Update tab when URL changes
  useEffect(() => {
    setActiveTab(getTabFromPath(location.pathname));
  }, [location.pathname]);

  useEffect(() => {
    fetchStats();
    fetchData();
  }, [activeTab, typeFilter, statusFilter]);

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/mou/stats/summary`, { headers });
      setStats(response.data);
    } catch (err) {
      console.error('Failed to fetch stats');
    }
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      if (activeTab === 'mous' || activeTab === 'dashboard') {
        let url = `${API_URL}/api/mou/`;
        const params = new URLSearchParams();
        if (typeFilter !== 'all') params.append('partner_type', typeFilter);
        if (statusFilter !== 'all') params.append('status', statusFilter);
        if (search) params.append('search', search);
        if (params.toString()) url += `?${params.toString()}`;
        
        const res = await axios.get(url, { headers });
        setMous(res.data.mous || []);
      }
      if (activeTab === 'activities') {
        const res = await axios.get(`${API_URL}/api/mou/activities/all`, { headers });
        setActivities(res.data.activities || []);
      }
    } catch (err) {
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  const handleMoUSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...mouForm,
        activities: mouForm.activities.length > 0 ? mouForm.activities : [],
        students_benefited: mouForm.students_benefited ? parseInt(mouForm.students_benefited) : 0,
        faculty_involved: mouForm.faculty_involved ? parseInt(mouForm.faculty_involved) : 0,
        duration_years: mouForm.duration_years ? parseInt(mouForm.duration_years) : null
      };
      
      if (editMode && selectedMoU) {
        await axios.put(`${API_URL}/api/mou/${selectedMoU.id}`, payload, { headers });
        toast.success('MoU updated successfully');
      } else {
        await axios.post(`${API_URL}/api/mou/`, payload, { headers });
        toast.success('MoU created successfully');
      }
      setShowMoUModal(false);
      resetMoUForm();
      fetchData();
      fetchStats();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save MoU');
    }
  };

  const handleActivitySubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...activityForm,
        participants_count: activityForm.participants_count ? parseInt(activityForm.participants_count) : 0
      };
      await axios.post(`${API_URL}/api/mou/activities`, payload, { headers });
      toast.success('Activity added successfully');
      setShowActivityModal(false);
      resetActivityForm();
      fetchData();
      fetchStats();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add activity');
    }
  };

  const handleDelete = async (type, id) => {
    if (!window.confirm('Are you sure you want to delete this?')) return;
    try {
      if (type === 'mou') {
        await axios.delete(`${API_URL}/api/mou/${id}`, { headers });
      } else {
        await axios.delete(`${API_URL}/api/mou/activities/${id}`, { headers });
      }
      toast.success('Deleted successfully');
      fetchData();
      fetchStats();
    } catch (err) {
      toast.error('Failed to delete');
    }
  };

  const openEditModal = (mou) => {
    setSelectedMoU(mou);
    setMouForm({
      title: mou.title || '',
      partner_name: mou.partner_name || '',
      partner_type: mou.partner_type || 'industry',
      partner_country: mou.partner_country || 'India',
      purpose: mou.purpose || '',
      scope: mou.scope || '',
      start_date: mou.start_date || '',
      end_date: mou.end_date || '',
      duration_years: mou.duration_years || '',
      status: mou.status || 'active',
      signatory_institution: mou.signatory_institution || '',
      signatory_partner: mou.signatory_partner || '',
      activities: mou.activities || [],
      outcomes: mou.outcomes || '',
      students_benefited: mou.students_benefited || '',
      faculty_involved: mou.faculty_involved || '',
      contact_person: mou.contact_person || '',
      contact_email: mou.contact_email || '',
      remarks: mou.remarks || ''
    });
    setEditMode(true);
    setShowMoUModal(true);
  };

  const openDetailModal = async (mou) => {
    try {
      const res = await axios.get(`${API_URL}/api/mou/${mou.id}`, { headers });
      setSelectedMoU(res.data);
      setShowDetailModal(true);
    } catch (err) {
      toast.error('Failed to fetch details');
    }
  };

  const resetMoUForm = () => {
    setMouForm({
      title: '', partner_name: '', partner_type: 'industry', partner_country: 'India',
      purpose: '', scope: '', start_date: '', end_date: '', duration_years: '',
      status: 'active', signatory_institution: '', signatory_partner: '',
      activities: [], outcomes: '', students_benefited: '', faculty_involved: '',
      contact_person: '', contact_email: '', remarks: ''
    });
    setEditMode(false);
    setSelectedMoU(null);
  };

  const resetActivityForm = () => {
    setActivityForm({
      mou_id: '', activity_type: 'workshop', title: '', description: '',
      date: '', participants_count: '', outcomes: ''
    });
  };

  const partnerTypeIcons = {
    industry: Building2,
    academia: GraduationCap,
    government: Landmark,
    ngo: Heart,
    international: Globe
  };

  const statusColors = {
    active: 'bg-green-100 text-green-800',
    expired: 'bg-red-100 text-red-800',
    renewed: 'bg-blue-100 text-blue-800',
    terminated: 'bg-gray-100 text-gray-800'
  };

  const partnerTypeColors = {
    industry: 'bg-blue-100 text-blue-800',
    academia: 'bg-purple-100 text-purple-800',
    government: 'bg-orange-100 text-orange-800',
    ngo: 'bg-pink-100 text-pink-800',
    international: 'bg-cyan-100 text-cyan-800'
  };

  const StatCard = ({ title, value, icon: Icon, color = 'blue', subtitle }) => (
    <Card>
      <CardContent className="p-4">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-gray-500">{title}</p>
            <p className="text-2xl font-bold">{value}</p>
            {subtitle && <p className="text-xs text-gray-400">{subtitle}</p>}
          </div>
          <div className={`w-12 h-12 bg-${color}-100 rounded-full flex items-center justify-center`}>
            <Icon className={`h-6 w-6 text-${color}-600`} />
          </div>
        </div>
      </CardContent>
    </Card>
  );

  return (
    <div className="p-6 space-y-6" data-testid="mou-management-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">MoU Management</h1>
          <p className="text-gray-500">Manage institutional collaborations & partnerships</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => { resetMoUForm(); setShowMoUModal(true); }}>
            <Handshake className="h-4 w-4 mr-1" /> New MoU
          </Button>
          <Button onClick={() => setShowActivityModal(true)}>
            <Activity className="h-4 w-4 mr-1" /> Add Activity
          </Button>
        </div>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
          <StatCard title="Total MoUs" value={stats.total_mous || 0} icon={Handshake} color="blue" />
          <StatCard title="Active" value={stats.active_mous || 0} icon={FileText} color="green" />
          <StatCard title="International" value={stats.international_mous || 0} icon={Globe} color="purple" />
          <StatCard title="Industry" value={stats.type_wise?.industry || 0} icon={Building2} color="orange" />
          <StatCard title="Students Benefited" value={stats.students_benefited || 0} icon={Users} color="cyan" />
          <StatCard title="Activities" value={stats.total_activities || 0} icon={Activity} color="pink" />
        </div>
      )}

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-4">
          <TabsList>
            <TabsTrigger value="mous">All MoUs</TabsTrigger>
            <TabsTrigger value="activities">Activities</TabsTrigger>
            <TabsTrigger value="dashboard">Analytics</TabsTrigger>
          </TabsList>
          
          {activeTab === 'mous' && (
            <div className="flex gap-2">
              <Select value={typeFilter} onValueChange={setTypeFilter}>
                <SelectTrigger className="w-[150px]"><SelectValue placeholder="Type" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Types</SelectItem>
                  <SelectItem value="industry">Industry</SelectItem>
                  <SelectItem value="academia">Academia</SelectItem>
                  <SelectItem value="government">Government</SelectItem>
                  <SelectItem value="ngo">NGO</SelectItem>
                  <SelectItem value="international">International</SelectItem>
                </SelectContent>
              </Select>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-[130px]"><SelectValue placeholder="Status" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="expired">Expired</SelectItem>
                  <SelectItem value="renewed">Renewed</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}
        </div>

        {/* MoUs Tab */}
        <TabsContent value="mous" className="mt-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {loading ? (
              <Card className="col-span-full"><CardContent className="p-8 text-center"><RefreshCw className="h-6 w-6 animate-spin mx-auto" /></CardContent></Card>
            ) : mous.length === 0 ? (
              <Card className="col-span-full"><CardContent className="p-8 text-center text-gray-500">No MoUs found</CardContent></Card>
            ) : (
              mous.map((mou) => {
                const TypeIcon = partnerTypeIcons[mou.partner_type] || Building;
                return (
                  <Card key={mou.id} className="hover:shadow-md transition-shadow">
                    <CardHeader className="pb-2">
                      <div className="flex justify-between items-start">
                        <div className="flex items-center gap-2">
                          <div className={`w-10 h-10 rounded-full flex items-center justify-center ${partnerTypeColors[mou.partner_type]}`}>
                            <TypeIcon className="h-5 w-5" />
                          </div>
                          <div>
                            <CardTitle className="text-base line-clamp-1">{mou.title}</CardTitle>
                            <p className="text-sm text-gray-500">{mou.partner_name}</p>
                          </div>
                        </div>
                        <Badge className={statusColors[mou.status]}>{mou.status}</Badge>
                      </div>
                    </CardHeader>
                    <CardContent className="space-y-2 text-sm">
                      <p className="text-gray-600 line-clamp-2">{mou.purpose}</p>
                      <div className="flex items-center gap-4 text-gray-500">
                        <span className="flex items-center gap-1">
                          <Calendar className="h-3 w-3" />
                          {mou.start_date}
                        </span>
                        {mou.partner_country !== 'India' && (
                          <span className="flex items-center gap-1">
                            <Globe className="h-3 w-3" />
                            {mou.partner_country}
                          </span>
                        )}
                      </div>
                      <div className="flex justify-between items-center pt-2">
                        <div className="flex gap-2 text-xs">
                          {mou.students_benefited > 0 && (
                            <span className="bg-gray-100 px-2 py-1 rounded">{mou.students_benefited} students</span>
                          )}
                        </div>
                        <div className="flex gap-1">
                          <Button variant="ghost" size="sm" onClick={() => openDetailModal(mou)}>
                            <Eye className="h-4 w-4" />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => openEditModal(mou)}>
                            <FileText className="h-4 w-4" />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => handleDelete('mou', mou.id)}>
                            <Trash2 className="h-4 w-4 text-red-500" />
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })
            )}
          </div>
        </TabsContent>

        {/* Activities Tab */}
        <TabsContent value="activities" className="mt-4">
          <Card>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left">Activity</th>
                      <th className="px-4 py-3 text-center">Type</th>
                      <th className="px-4 py-3 text-center">Date</th>
                      <th className="px-4 py-3 text-center">Participants</th>
                      <th className="px-4 py-3 text-center">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {activities.length === 0 ? (
                      <tr><td colSpan="5" className="text-center py-8 text-gray-500">No activities found</td></tr>
                    ) : (
                      activities.map((act) => (
                        <tr key={act.id} className="border-t hover:bg-gray-50">
                          <td className="px-4 py-3">
                            <p className="font-medium">{act.title}</p>
                            <p className="text-xs text-gray-500">{act.description}</p>
                          </td>
                          <td className="px-4 py-3 text-center">
                            <Badge variant="outline">{act.activity_type?.replace('_', ' ')}</Badge>
                          </td>
                          <td className="px-4 py-3 text-center">{act.date}</td>
                          <td className="px-4 py-3 text-center">{act.participants_count || '-'}</td>
                          <td className="px-4 py-3 text-center">
                            <Button variant="ghost" size="sm" onClick={() => handleDelete('activity', act.id)}>
                              <Trash2 className="h-4 w-4 text-red-500" />
                            </Button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Analytics Tab */}
        <TabsContent value="dashboard" className="mt-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Type Distribution */}
            <Card>
              <CardHeader><CardTitle className="text-lg">Partner Type Distribution</CardTitle></CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {stats?.type_wise && Object.entries(stats.type_wise).map(([type, count]) => (
                    <div key={type} className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Badge className={partnerTypeColors[type]}>{type}</Badge>
                      </div>
                      <span className="font-semibold">{count}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Activity Types */}
            <Card>
              <CardHeader><CardTitle className="text-lg">Activity Types</CardTitle></CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {stats?.activity_types && Object.entries(stats.activity_types).map(([type, count]) => (
                    <div key={type} className="flex items-center justify-between">
                      <span className="text-gray-600 capitalize">{type.replace('_', ' ')}</span>
                      <span className="font-semibold">{count}</span>
                    </div>
                  ))}
                  {(!stats?.activity_types || Object.keys(stats.activity_types).length === 0) && (
                    <p className="text-gray-500">No activities recorded yet</p>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Year-wise Trend */}
            <Card className="md:col-span-2">
              <CardHeader><CardTitle className="text-lg">Year-wise MoU Trend</CardTitle></CardHeader>
              <CardContent>
                <div className="flex gap-4 flex-wrap">
                  {stats?.year_wise && Object.entries(stats.year_wise).sort((a, b) => b[0] - a[0]).map(([year, count]) => (
                    <div key={year} className="bg-gray-100 px-4 py-2 rounded-lg text-center">
                      <p className="text-2xl font-bold text-blue-600">{count}</p>
                      <p className="text-sm text-gray-500">{year}</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>

      {/* MoU Modal */}
      <Dialog open={showMoUModal} onOpenChange={setShowMoUModal}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editMode ? 'Edit MoU' : 'Add New MoU'}</DialogTitle></DialogHeader>
          <form onSubmit={handleMoUSubmit} className="space-y-4">
            <div>
              <label className="text-sm font-medium">MoU Title *</label>
              <Input value={mouForm.title} onChange={(e) => setMouForm({...mouForm, title: e.target.value})} required />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium">Partner Organization *</label>
                <Input value={mouForm.partner_name} onChange={(e) => setMouForm({...mouForm, partner_name: e.target.value})} required />
              </div>
              <div>
                <label className="text-sm font-medium">Partner Type *</label>
                <Select value={mouForm.partner_type} onValueChange={(v) => setMouForm({...mouForm, partner_type: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="industry">Industry</SelectItem>
                    <SelectItem value="academia">Academia</SelectItem>
                    <SelectItem value="government">Government</SelectItem>
                    <SelectItem value="ngo">NGO</SelectItem>
                    <SelectItem value="international">International</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium">Country</label>
                <Input value={mouForm.partner_country} onChange={(e) => setMouForm({...mouForm, partner_country: e.target.value})} />
              </div>
              <div>
                <label className="text-sm font-medium">Status</label>
                <Select value={mouForm.status} onValueChange={(v) => setMouForm({...mouForm, status: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="expired">Expired</SelectItem>
                    <SelectItem value="renewed">Renewed</SelectItem>
                    <SelectItem value="terminated">Terminated</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <label className="text-sm font-medium">Purpose *</label>
              <Textarea value={mouForm.purpose} onChange={(e) => setMouForm({...mouForm, purpose: e.target.value})} required />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium">Start Date *</label>
                <Input type="date" value={mouForm.start_date} onChange={(e) => setMouForm({...mouForm, start_date: e.target.value})} required />
              </div>
              <div>
                <label className="text-sm font-medium">End Date</label>
                <Input type="date" value={mouForm.end_date} onChange={(e) => setMouForm({...mouForm, end_date: e.target.value})} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium">Students Benefited</label>
                <Input type="number" value={mouForm.students_benefited} onChange={(e) => setMouForm({...mouForm, students_benefited: e.target.value})} />
              </div>
              <div>
                <label className="text-sm font-medium">Faculty Involved</label>
                <Input type="number" value={mouForm.faculty_involved} onChange={(e) => setMouForm({...mouForm, faculty_involved: e.target.value})} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium">Contact Person</label>
                <Input value={mouForm.contact_person} onChange={(e) => setMouForm({...mouForm, contact_person: e.target.value})} />
              </div>
              <div>
                <label className="text-sm font-medium">Contact Email</label>
                <Input type="email" value={mouForm.contact_email} onChange={(e) => setMouForm({...mouForm, contact_email: e.target.value})} />
              </div>
            </div>
            <div>
              <label className="text-sm font-medium">Outcomes</label>
              <Textarea value={mouForm.outcomes} onChange={(e) => setMouForm({...mouForm, outcomes: e.target.value})} placeholder="Key outcomes achieved..." />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => { setShowMoUModal(false); resetMoUForm(); }}>Cancel</Button>
              <Button type="submit">{editMode ? 'Update' : 'Create'} MoU</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Activity Modal */}
      <Dialog open={showActivityModal} onOpenChange={setShowActivityModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>Add MoU Activity</DialogTitle></DialogHeader>
          <form onSubmit={handleActivitySubmit} className="space-y-4">
            <div>
              <label className="text-sm font-medium">Select MoU *</label>
              <Select value={activityForm.mou_id} onValueChange={(v) => setActivityForm({...activityForm, mou_id: v})}>
                <SelectTrigger><SelectValue placeholder="Select MoU" /></SelectTrigger>
                <SelectContent>
                  {mous.map((mou) => (
                    <SelectItem key={mou.id} value={mou.id}>{mou.title} - {mou.partner_name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-medium">Activity Type *</label>
              <Select value={activityForm.activity_type} onValueChange={(v) => setActivityForm({...activityForm, activity_type: v})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="student_exchange">Student Exchange</SelectItem>
                  <SelectItem value="faculty_exchange">Faculty Exchange</SelectItem>
                  <SelectItem value="internship">Internship</SelectItem>
                  <SelectItem value="workshop">Workshop</SelectItem>
                  <SelectItem value="joint_research">Joint Research</SelectItem>
                  <SelectItem value="guest_lecture">Guest Lecture</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-medium">Activity Title *</label>
              <Input value={activityForm.title} onChange={(e) => setActivityForm({...activityForm, title: e.target.value})} required />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium">Date *</label>
                <Input type="date" value={activityForm.date} onChange={(e) => setActivityForm({...activityForm, date: e.target.value})} required />
              </div>
              <div>
                <label className="text-sm font-medium">Participants</label>
                <Input type="number" value={activityForm.participants_count} onChange={(e) => setActivityForm({...activityForm, participants_count: e.target.value})} />
              </div>
            </div>
            <div>
              <label className="text-sm font-medium">Description</label>
              <Textarea value={activityForm.description} onChange={(e) => setActivityForm({...activityForm, description: e.target.value})} />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => { setShowActivityModal(false); resetActivityForm(); }}>Cancel</Button>
              <Button type="submit">Add Activity</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Detail Modal */}
      <Dialog open={showDetailModal} onOpenChange={setShowDetailModal}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>MoU Details</DialogTitle></DialogHeader>
          {selectedMoU && (
            <div className="space-y-4">
              <div className="flex justify-between items-start">
                <div>
                  <h3 className="text-xl font-bold">{selectedMoU.title}</h3>
                  <p className="text-gray-500">{selectedMoU.partner_name}</p>
                </div>
                <Badge className={statusColors[selectedMoU.status]}>{selectedMoU.status}</Badge>
              </div>
              
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-gray-500">Partner Type:</span>
                  <p className="font-medium capitalize">{selectedMoU.partner_type}</p>
                </div>
                <div>
                  <span className="text-gray-500">Country:</span>
                  <p className="font-medium">{selectedMoU.partner_country}</p>
                </div>
                <div>
                  <span className="text-gray-500">Start Date:</span>
                  <p className="font-medium">{selectedMoU.start_date}</p>
                </div>
                <div>
                  <span className="text-gray-500">End Date:</span>
                  <p className="font-medium">{selectedMoU.end_date || 'Ongoing'}</p>
                </div>
                <div>
                  <span className="text-gray-500">Students Benefited:</span>
                  <p className="font-medium">{selectedMoU.students_benefited || 0}</p>
                </div>
                <div>
                  <span className="text-gray-500">Faculty Involved:</span>
                  <p className="font-medium">{selectedMoU.faculty_involved || 0}</p>
                </div>
              </div>
              
              <div>
                <span className="text-gray-500 text-sm">Purpose:</span>
                <p className="mt-1">{selectedMoU.purpose}</p>
              </div>
              
              {selectedMoU.outcomes && (
                <div>
                  <span className="text-gray-500 text-sm">Outcomes:</span>
                  <p className="mt-1">{selectedMoU.outcomes}</p>
                </div>
              )}
              
              {selectedMoU.activities_list?.length > 0 && (
                <div>
                  <span className="text-gray-500 text-sm">Related Activities:</span>
                  <div className="mt-2 space-y-2">
                    {selectedMoU.activities_list.map((act) => (
                      <div key={act.id} className="bg-gray-50 p-2 rounded">
                        <p className="font-medium">{act.title}</p>
                        <p className="text-xs text-gray-500">{act.activity_type} • {act.date}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
